import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  showDragHandle?: boolean;
  showCloseButton?: boolean;
  title?: string;
  snapPoints?: {
    default: number;
    full: number;
  };
}

// Animation constants
const ANIMATION_CONFIG = {
  duration: 300,
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
} as const;

// Gesture constants
const GESTURE_CONFIG = {
  velocityThreshold: 0.1,
  dragThreshold: 15,
  positionThreshold: 40,
  resistanceFactor: 0.15
} as const;

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  className = '',
  showDragHandle = true,
  showCloseButton = true,
  title,
  snapPoints = {
    default: window.innerHeight * 0.45,
    full: window.innerHeight
  }
}) => {
  // Refs
  const sheetRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef({
    touchStartY: 0,
    lastTouchY: 0,
    currentHeight: 0,
    velocity: 0,
    lastTime: 0,
    isDragging: false
  });
  const animationFrame = useRef<number>();

  // State
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Animate sheet to target height
  const animateSheet = useCallback((targetHeight: number, shouldClose: boolean = false) => {
    if (!sheetRef.current) return;

    const startHeight = sheetRef.current.getBoundingClientRect().height;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / ANIMATION_CONFIG.duration, 1);
      
      // Smooth easing function
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      const currentHeight = startHeight + (targetHeight - startHeight) * easeProgress;
      
      sheetRef.current!.style.height = `${currentHeight}px`;

      if (progress < 1) {
        animationFrame.current = requestAnimationFrame(animate);
      } else {
        setIsFullScreen(targetHeight === snapPoints.full);
        if (shouldClose) onClose();
      }
    };

    cancelAnimationFrame(animationFrame.current!);
    animationFrame.current = requestAnimationFrame(animate);
  }, [onClose, snapPoints.full]);

  // Determine next snap point based on current position and gesture
  const getNextSnapPoint = useCallback((currentHeight: number, velocity: number, deltaY: number) => {
    const { velocityThreshold, positionThreshold } = GESTURE_CONFIG;
    const isMovingUp = deltaY < 0 || velocity > velocityThreshold;
    const isMovingDown = deltaY > 0 || velocity < -velocityThreshold;

    // Helper to determine current position
    const getCurrentPosition = () => {
      if (currentHeight >= snapPoints.full - positionThreshold) return 'full';
      if (Math.abs(currentHeight - snapPoints.default) < positionThreshold) return 'default';
      if (currentHeight <= positionThreshold) return 'closed';
      return currentHeight > (snapPoints.full + snapPoints.default) / 2 ? 'full' : 'default';
    };

    const position = getCurrentPosition();

    // Sequential state transitions
    switch (position) {
      case 'full':
        return isMovingDown 
          ? { height: snapPoints.default, shouldClose: false }
          : { height: snapPoints.full, shouldClose: false };
      case 'default':
        if (isMovingUp) return { height: snapPoints.full, shouldClose: false };
        if (isMovingDown) return { height: 0, shouldClose: true };
        return { height: snapPoints.default, shouldClose: false };
      default:
        return isMovingUp
          ? { height: snapPoints.default, shouldClose: false }
          : { height: 0, shouldClose: true };
    }
  }, [snapPoints]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    const { current: gesture } = gestureRef;
    
    gesture.touchStartY = touch.clientY;
    gesture.lastTouchY = touch.clientY;
    gesture.lastTime = performance.now();
    gesture.currentHeight = sheetRef.current?.getBoundingClientRect().height || 0;
    gesture.isDragging = false;
    gesture.velocity = 0;

    cancelAnimationFrame(animationFrame.current!);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!sheetRef.current) return;
    
    const { current: gesture } = gestureRef;
    const touch = e.touches[0];
    const currentY = touch.clientY;
    const deltaY = currentY - gesture.touchStartY;
    const currentTime = performance.now();
    
    // Update velocity calculation
    const timeDelta = currentTime - gesture.lastTime;
    if (timeDelta > 0) {
      gesture.velocity = (gesture.lastTouchY - currentY) / timeDelta;
    }

    if (!gesture.isDragging && Math.abs(deltaY) > GESTURE_CONFIG.dragThreshold) {
      gesture.isDragging = true;
    }

    if (gesture.isDragging) {
      const newHeight = Math.max(0, Math.min(snapPoints.full, gesture.currentHeight - deltaY));
      
      // Add resistance at extremes
      if (newHeight > snapPoints.full * 0.95) {
        const overshoot = newHeight - snapPoints.full;
        sheetRef.current.style.height = `${snapPoints.full + overshoot * GESTURE_CONFIG.resistanceFactor}px`;
      } else {
        sheetRef.current.style.height = `${newHeight}px`;
      }
    }

    gesture.lastTouchY = currentY;
    gesture.lastTime = currentTime;
  }, [snapPoints]);

  const handleTouchEnd = useCallback(() => {
    const { current: gesture } = gestureRef;
    if (!sheetRef.current || !gesture.isDragging) return;

    const currentHeight = sheetRef.current.getBoundingClientRect().height;
    const deltaY = gesture.lastTouchY - gesture.touchStartY;
    
    const { height, shouldClose } = getNextSnapPoint(currentHeight, gesture.velocity, deltaY);
    animateSheet(height, shouldClose);
    
    gesture.isDragging = false;
    gesture.velocity = 0;
  }, [getNextSnapPoint, animateSheet]);

  // Handle open/close
  useEffect(() => {
    animateSheet(isOpen ? snapPoints.default : 0, !isOpen);
  }, [isOpen, animateSheet, snapPoints.default]);

  // Cleanup
  useEffect(() => () => {
    cancelAnimationFrame(animationFrame.current!);
  }, []);

  return (
    <div 
      className={`
        lg:hidden fixed bottom-0 left-0 right-0 
        bg-surface-primary
        transform 
        transition-[transform,border-radius] duration-300 ease-out
        ${isOpen ? 'translate-y-0' : 'translate-y-full'}
        ${isFullScreen ? 'h-screen rounded-none' : `h-[${snapPoints.default}px] rounded-t-[24px]`}
        shadow-sm
        z-50
        overflow-hidden
        ${className}
      `}
      ref={sheetRef}
    >
      {showDragHandle && (
        <div 
          className={`
            sticky top-0 w-full 
            cursor-grab active:cursor-grabbing 
            transition-[border-radius] duration-300 ease-out
            ${isFullScreen ? 'rounded-none' : 'rounded-t-[24px]'}
            bg-surface-primary
            px-4 py-3
            border-b border-border-primary
          `}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="relative flex flex-col items-center w-full">
            <div className="w-10 h-1 bg-border-primary opacity-60 rounded-full mb-3" />
            <div className="relative flex items-center justify-between w-full">
              {title && (
                <h2 className="text-base font-medium text-text-primary ps-2">
                  {title}
                </h2>
              )}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-surface-secondary/80 active:bg-surface-secondary flex items-center justify-center"
                  aria-label="Close bottom sheet"
                >
                  <Icon icon="ph:x-thin" className="w-5 h-5 text-text-primary" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="h-[calc(100%-56px)] overflow-y-auto overscroll-contain px-4 pt-4 pb-10">
        {children}
      </div>
    </div>
  );
};

export default BottomSheet; 